/**
 * Palette Selector — custom element <palette-selector>
 * Vanilla JS, aucune dépendance externe, aucun bundler.
 *
 * Intégration panier : un seul POST /cart/add.js avec tous les items,
 * suivi d'un événement 'shopify:cart:lines-update' qui déclenche
 * la mise à jour du cart drawer et du compteur du thème Tinker.
 */

/* ──────────────────────────────────────────────────────────────────
   Synthetic CartLinesUpdateEvent compatible avec @shopify/events
   L'événement doit avoir :
     - .action  (string)   pour le cart drawer (auto-open)
     - .promise (Promise)  pour la résolution du refresh de contenu
   ────────────────────────────────────────────────────────────────── */
class PaletteCartUpdateEvent extends CustomEvent {
  constructor(promise) {
    super('shopify:cart:lines-update', { bubbles: true, composed: true });
    this.action = 'add';
    this.promise = promise;
  }
}

/* ──────────────────────────────────────────────────────────────────
   PaletteSelector
   ────────────────────────────────────────────────────────────────── */
class PaletteSelector extends HTMLElement {
  /** @type {Array<object>} */
  #variants = [];

  /** @type {Map<number, number>} variantId → quantité */
  #selections = new Map();

  /** @type {string} */
  #searchQuery = '';

  /** @type {string} */
  #activeFamily = 'all';

  /** @type {HTMLDialogElement|null} */
  #dialog = null;

  /** @type {HTMLElement|null} */
  #grid = null;

  /** @type {HTMLButtonElement|null} */
  #openBtn = null;

  /** @type {HTMLButtonElement|null} */
  #addBtn = null;

  /** @type {HTMLDialogElement|null} */
  #lightbox = null;

  connectedCallback() {
    const dataEl = this.querySelector('.palette-selector__data');
    if (!dataEl) return;

    try {
      this.#variants = JSON.parse(dataEl.textContent || '[]');
    } catch {
      console.error('[palette-selector] Impossible de parser les données variantes.');
      return;
    }

    this.#dialog = this.querySelector('.palette-selector__dialog');
    this.#grid   = this.querySelector('.palette-selector__grid');
    this.#openBtn = this.querySelector('.palette-selector__open-btn');
    this.#addBtn  = this.querySelector('.palette-selector__add-btn');

    this.#bindTrigger();
    this.#bindDialog();
    this.#bindSearch();
    this.#bindFamilies();
    this.#bindFooter();
    this.#injectLightbox();

    this.#renderGrid();
    this.#updateSummary();
  }

  /* ── Lightbox ───────────────────────────────────────────────────── */

  #injectLightbox() {
    const el = document.createElement('dialog');
    el.className = 'palette-selector__lightbox';
    el.innerHTML = `
      <button class="palette-selector__lightbox-close" type="button" aria-label="Fermer l'aperçu">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
      <div class="palette-selector__lightbox-loader" aria-hidden="true"></div>
      <img class="palette-selector__lightbox-img" src="" alt="">
      <p class="palette-selector__lightbox-label"></p>
    `;

    // Attaché au body pour éviter la restriction showModal() dans un dialog parent
    document.body.appendChild(el);
    this.#lightbox = el;

    el.querySelector('.palette-selector__lightbox-close')
      ?.addEventListener('click', () => el.close());

    el.addEventListener('click', (e) => {
      if (e.target === el) el.close();
    });
  }

  disconnectedCallback() {
    this.#lightbox?.remove();
    this.#lightbox = null;
  }

  #openLightbox(swatchValue, label = '') {
    if (!this.#lightbox) return;
    const img    = this.#lightbox.querySelector('.palette-selector__lightbox-img');
    const loader = this.#lightbox.querySelector('.palette-selector__lightbox-loader');
    const lbl    = this.#lightbox.querySelector('.palette-selector__lightbox-label');
    const hiRes  = swatchValue.replace(/width=\d+/, 'width=900');

    if (img) {
      // Affiche le loader, masque l'image pendant le chargement
      loader?.classList.add('palette-selector__lightbox-loader--visible');
      img.classList.add('palette-selector__lightbox-img--loading');

      const onLoad = () => {
        loader?.classList.remove('palette-selector__lightbox-loader--visible');
        img.classList.remove('palette-selector__lightbox-img--loading');
      };
      img.addEventListener('load', onLoad, { once: true });
      img.addEventListener('error', onLoad, { once: true });

      img.src = hiRes;
      img.alt = label;
    }

    if (lbl) lbl.textContent = label;

    if (!this.#lightbox.open) this.#lightbox.showModal();
  }

  /* ── Trigger (bouton + pastilles d'aperçu) ─────────────────────── */

  #bindTrigger() {
    this.#openBtn?.addEventListener('click', () => this.#openDialog());

    this.querySelectorAll('.palette-selector__preview-swatch').forEach(btn => {
      btn.addEventListener('click', () => this.#openDialog());
    });
  }

  /* ── Dialog ─────────────────────────────────────────────────────── */

  #bindDialog() {
    // Bouton fermer
    this.querySelector('[data-ps-action="close"]')?.addEventListener('click', () => this.#closeDialog());

    // Clic sur le backdrop (zone hors dialog-inner)
    this.#dialog?.addEventListener('click', (e) => {
      if (e.target === this.#dialog) this.#closeDialog();
    });

    // Touche Échap : géré nativement par <dialog>, on écoute l'événement close
    this.#dialog?.addEventListener('close', () => {
      document.body.style.overflow = '';
      this.#openBtn?.focus();
    });
  }

  #openDialog() {
    this.#dialog?.showModal();
    document.body.style.overflow = 'hidden';
    // Focus sur la recherche
    requestAnimationFrame(() => {
      this.querySelector('.palette-selector__search')?.focus();
    });
  }

  #closeDialog() {
    this.#dialog?.close();
    // overflow restored via 'close' event
  }

  /* ── Recherche ──────────────────────────────────────────────────── */

  #bindSearch() {
    const input = this.querySelector('.palette-selector__search');
    input?.addEventListener('input', (e) => {
      this.#searchQuery = /** @type {HTMLInputElement} */ (e.target).value.trim().toLowerCase();
      this.#renderGrid();
    });
  }

  /* ── Filtres famille ────────────────────────────────────────────── */

  #bindFamilies() {
    this.querySelector('.palette-selector__families')?.addEventListener('click', (e) => {
      const chip = /** @type {HTMLElement} */ (e.target).closest('.palette-selector__family-chip');
      if (!chip) return;

      this.querySelectorAll('.palette-selector__family-chip').forEach(c => {
        c.classList.remove('palette-selector__family-chip--active');
        c.removeAttribute('aria-pressed');
      });

      chip.classList.add('palette-selector__family-chip--active');
      chip.setAttribute('aria-pressed', 'true');
      this.#activeFamily = chip.dataset.family ?? 'all';
      this.#renderGrid();
    });
  }

  /* ── Footer (récap + ajout) ────────────────────────────────────── */

  #bindFooter() {
    // Toggle récapitulatif
    const recapBtn    = this.querySelector('.palette-selector__recap-btn');
    const recapDetail = this.querySelector('.palette-selector__recap-detail');

    recapBtn?.addEventListener('click', () => {
      const expanded = recapBtn.getAttribute('aria-expanded') === 'true';
      recapBtn.setAttribute('aria-expanded', String(!expanded));
      recapDetail?.classList.toggle('palette-selector__hidden', expanded);
    });

    // Ajout au panier
    this.#addBtn?.addEventListener('click', () => this.#addToCart());
  }

  /* ── Grille ─────────────────────────────────────────────────────── */

  #filteredVariants() {
    const q = this.#searchQuery;
    return this.#variants
      .filter(v => {
        const matchSearch = !q
          || v.title.toLowerCase().includes(q)
          || v.colorName.toLowerCase().includes(q);
        const matchFamily = this.#activeFamily === 'all'
          || v.colorFamily === this.#activeFamily;
        return matchSearch && matchFamily;
      })
      .sort((a, b) => {
        // Ruptures de stock en fin de grille
        if (a.available && !b.available) return -1;
        if (!a.available && b.available) return 1;
        return 0;
      });
  }

  #renderGrid() {
    if (!this.#grid) return;

    const list = this.#filteredVariants();

    if (list.length === 0) {
      this.#grid.innerHTML = `
        <p class="palette-selector__empty" role="status">
          Aucune couleur ne correspond à votre recherche.
        </p>`;
      return;
    }

    this.#grid.innerHTML = list.map(v => this.#cardHTML(v)).join('');

    // Restaurer les quantités déjà saisies
    this.#selections.forEach((qty, id) => {
      if (qty <= 0) return;
      const card = this.#grid.querySelector(`[data-variant-id="${id}"]`);
      if (card) this.#syncCardDOM(card, id, qty);
    });

    // Délégation des steppers
    this.#grid.addEventListener('click', this.#onGridClick, { signal: this.#abortCtrl?.signal });
  }

  /** Contrôleur AbortController pour le listener délégué (évite les doublons) */
  #abortCtrl = new AbortController();

  #onGridClick = (e) => {
    const target = /** @type {HTMLElement} */ (e.target);

    // Clic sur stepper
    const btn = target.closest('.palette-selector__stepper-btn');
    if (btn) {
      const card = btn.closest('.palette-selector__card');
      if (!card) return;
      const variantId = parseInt(card.dataset.variantId ?? '0');
      const action    = btn.dataset.psAction;
      if (action === 'inc') this.#changeQty(variantId, 1, card);
      if (action === 'dec') this.#changeQty(variantId, -1, card);
      return;
    }

    // Clic sur swatch image → lightbox
    const swatch = target.closest('.palette-selector__swatch--clickable');
    if (swatch) {
      const card = swatch.closest('.palette-selector__card');
      if (!card) return;
      const variantId = parseInt(card.dataset.variantId ?? '0');
      const v = this.#variants.find(x => x.id === variantId);
      if (v) this.#openLightbox(v.swatchValue, v.colorName || v.title);
    }
  };

  #cardHTML(v) {
    const qty         = this.#selections.get(v.id) || 0;
    const isSelected  = qty > 0;
    const isUnavailable = !v.available;

    let swatchStyle = 'background-color:#e0e0e0;';
    let swatchInner = '';

    if (v.swatchType === 'hex') {
      swatchStyle = `background-color:${v.swatchValue};`;
    } else if (v.swatchType === 'image') {
      swatchInner = `<img class="palette-selector__swatch-img" src="${v.swatchValue}" alt="">`;
    }

    const accessibleLabel = v.colorName !== v.title
      ? `${v.title}, ${v.colorName}`
      : v.title;
    const qtyLabel = qty > 0 ? `, ${qty} sélectionnée${qty > 1 ? 's' : ''}` : '';

    return `<div
        class="palette-selector__card${isSelected ? ' palette-selector__card--selected' : ''}${isUnavailable ? ' palette-selector__card--unavailable' : ''}"
        role="listitem"
        data-variant-id="${v.id}"
        aria-label="${accessibleLabel}${qtyLabel}"
      >
        <div class="palette-selector__swatch${v.swatchType === 'image' ? ' palette-selector__swatch--clickable' : ''}" style="${swatchStyle}" aria-hidden="true">
          ${swatchInner}
          ${isUnavailable ? '<span class="palette-selector__unavailable-badge">Épuisé</span>' : ''}
        </div>
        <div class="palette-selector__card-info">
          <div class="palette-selector__card-code-row">
            <span class="palette-selector__card-code">${this.#esc(v.title)}</span>
            <span class="palette-selector__card-price">${this.#money(v.price)}</span>
          </div>
          ${v.colorName !== v.title ? `<span class="palette-selector__card-name">${this.#esc(v.colorName)}</span>` : ''}
        </div>
        <div class="palette-selector__stepper" role="group" aria-label="Quantité — ${this.#esc(accessibleLabel)}">
          <button
            class="palette-selector__stepper-btn"
            type="button"
            data-ps-action="dec"
            aria-label="Retirer une bobine de ${this.#esc(accessibleLabel)}"
            ${qty <= 0 || isUnavailable ? 'disabled' : ''}
          >−</button>
          <span
            class="palette-selector__stepper-count"
            aria-live="polite"
            aria-atomic="true"
          >${qty}</span>
          <button
            class="palette-selector__stepper-btn"
            type="button"
            data-ps-action="inc"
            aria-label="Ajouter une bobine de ${this.#esc(accessibleLabel)}"
            ${isUnavailable ? 'disabled aria-disabled="true"' : ''}
          >+</button>
        </div>
        <p class="palette-selector__card-error" role="alert" aria-live="assertive"></p>
      </div>`;
  }

  /* ── Stepper ──────────────────────────────────────────────────── */

  #changeQty(variantId, delta, card) {
    const v = this.#variants.find(x => x.id === variantId);
    if (!v || !v.available) return;

    const current = this.#selections.get(variantId) || 0;
    const next    = Math.max(0, current + delta);

    // Vérification stock côté client (bloquant uniquement si politique "deny")
    if (delta > 0 && v.inventoryPolicy !== 'continue' && v.inventoryQuantity > 0 && next > v.inventoryQuantity) {
      const errorEl = card.querySelector('.palette-selector__card-error');
      if (errorEl) {
        errorEl.textContent = `Stock max : ${v.inventoryQuantity}`;
        errorEl.classList.add('palette-selector__card-error--visible');
        setTimeout(() => errorEl.classList.remove('palette-selector__card-error--visible'), 3000);
      }
      return;
    }

    if (next === 0) {
      this.#selections.delete(variantId);
    } else {
      this.#selections.set(variantId, next);
    }

    this.#syncCardDOM(card, variantId, next);
    this.#updateSummary();
  }

  #syncCardDOM(card, variantId, qty) {
    const countEl  = card.querySelector('.palette-selector__stepper-count');
    const decBtn   = card.querySelector('[data-ps-action="dec"]');

    if (countEl) countEl.textContent = String(qty);
    if (decBtn)  decBtn.disabled = qty <= 0;

    card.classList.toggle('palette-selector__card--selected', qty > 0);

    // Mise à jour aria-label
    const v = this.#variants.find(x => x.id === variantId);
    if (v) {
      const base  = v.colorName !== v.title ? `${v.title}, ${v.colorName}` : v.title;
      const extra = qty > 0 ? `, ${qty} sélectionnée${qty > 1 ? 's' : ''}` : '';
      card.setAttribute('aria-label', base + extra);
    }

    // Effacer l'erreur si on revient à 0
    if (qty === 0) {
      card.querySelector('.palette-selector__card-error')
          ?.classList.remove('palette-selector__card-error--visible');
    }
  }

  /* ── Récapitulatif ──────────────────────────────────────────────── */

  #updateSummary() {
    let totalColors = 0, totalBobines = 0, totalCents = 0;

    this.#selections.forEach((qty, id) => {
      if (qty <= 0) return;
      const v = this.#variants.find(x => x.id === id);
      if (!v) return;
      totalColors++;
      totalBobines += qty;
      totalCents   += v.price * qty;
    });

    // Texte récap
    const colorsEl = this.querySelector('.palette-selector__recap-colors');
    const totalEl  = this.querySelector('.palette-selector__recap-total');

    if (colorsEl) colorsEl.textContent = `${totalColors} couleur${totalColors !== 1 ? 's' : ''} · ${totalBobines} bobine${totalBobines !== 1 ? 's' : ''}`;
    if (totalEl)  totalEl.textContent  = this.#money(totalCents);

    // Bouton ajouter
    if (this.#addBtn) {
      this.#addBtn.disabled = totalColors === 0;
      this.#addBtn.setAttribute('aria-label',
        totalColors > 0
          ? `Ajouter ${totalColors} couleur${totalColors !== 1 ? 's' : ''} au panier — ${this.#money(totalCents)}`
          : 'Ajouter la palette au panier'
      );
    }

    this.#renderRecapDetail();
  }

  #renderRecapDetail() {
    const detail = this.querySelector('.palette-selector__recap-detail');
    if (!detail) return;

    const lines = [];
    this.#selections.forEach((qty, id) => {
      if (qty <= 0) return;
      const v = this.#variants.find(x => x.id === id);
      if (v) lines.push({ v, qty, id });
    });

    detail.innerHTML = lines.map(({ v, qty, id }) => `
      <div class="palette-selector__recap-line">
        <span class="palette-selector__recap-line-label">${this.#esc(v.title)} × ${qty}</span>
        <span class="palette-selector__recap-line-price">${this.#money(v.price * qty)}</span>
        <button
          class="palette-selector__recap-line-remove"
          type="button"
          aria-label="Retirer ${this.#esc(v.title)} de la palette"
          data-variant-id="${id}"
          data-ps-action="remove"
        >×</button>
      </div>
    `).join('');

    detail.querySelectorAll('[data-ps-action="remove"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = parseInt(btn.dataset.variantId ?? '0');
        this.#selections.delete(id);
        const card = this.#grid?.querySelector(`[data-variant-id="${id}"]`);
        if (card) this.#syncCardDOM(card, id, 0);
        this.#updateSummary();
      });
    });
  }

  /* ── Ajout au panier ────────────────────────────────────────────── */

  async #addToCart() {
    const items = [];
    this.#selections.forEach((qty, id) => {
      if (qty > 0) items.push({ id, quantity: qty });
    });
    if (!items.length) return;

    // État chargement
    this.#addBtn?.classList.add('palette-selector__add-btn--loading');
    if (this.#addBtn) this.#addBtn.disabled = true;

    // Promesse déférée pour le cart event
    let resolveCart, rejectCart;
    const cartEventPromise = new Promise((res, rej) => {
      resolveCart = res;
      rejectCart  = rej;
    });

    // Dispatch : ouvre le cart drawer et met à jour le compteur
    document.dispatchEvent(new PaletteCartUpdateEvent(cartEventPromise));

    try {
      const res  = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();

      if (!res.ok) {
        this.#handleApiError(data);
        rejectCart(new Error(data.message ?? 'Add to cart failed'));
        return;
      }

      // Récupérer le panier mis à jour pour résoudre la promesse
      const cart = await fetch('/cart.js').then(r => r.json());
      resolveCart({
        cart: {
          id: cart.token,
          totalQuantity: cart.item_count,
          cost: {
            totalAmount: {
              amount: String(cart.total_price / 100),
              currencyCode: cart.currency,
            },
          },
          lines: [],
          discountCodes: [],
        },
        detail: { didError: false, source: 'palette-selector', items: cart.items },
      });

      // Animation bouton (même pattern que AddToCartComponent.animateAddToCart)
      if (this.#addBtn) {
        this.#addBtn.dataset.added = 'true';
        setTimeout(() => this.#addBtn?.removeAttribute('data-added'), 1500);
      }

      // Succès : confirmation discrète + reset
      const addedCount = items.reduce((acc, i) => acc + i.quantity, 0);
      this.#showSuccess(addedCount);
      this.#selections.clear();

      // Réinitialiser la recherche et les filtres
      const searchInput = this.querySelector('.palette-selector__search');
      if (searchInput) /** @type {HTMLInputElement} */ (searchInput).value = '';
      this.#searchQuery  = '';
      this.#activeFamily = 'all';
      this.querySelectorAll('.palette-selector__family-chip').forEach((c, i) => {
        c.classList.toggle('palette-selector__family-chip--active', i === 0);
        i === 0 ? c.setAttribute('aria-pressed', 'true') : c.removeAttribute('aria-pressed');
      });

      this.#renderGrid();
      this.#updateSummary();

    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        rejectCart(err);
        this.#showGlobalError('Erreur réseau. Veuillez réessayer.');
      }
    } finally {
      this.#addBtn?.classList.remove('palette-selector__add-btn--loading');
      if (this.#addBtn) this.#addBtn.disabled = this.#selections.size === 0;
    }
  }

  #handleApiError(data) {
    const errors = data.errors ?? {};

    if (Object.keys(errors).length > 0) {
      Object.entries(errors).forEach(([key, msgs]) => {
        const id = parseInt(key);
        if (!isNaN(id)) {
          const card    = this.#grid?.querySelector(`[data-variant-id="${id}"]`);
          const errorEl = card?.querySelector('.palette-selector__card-error');
          if (errorEl) {
            errorEl.textContent = Array.isArray(msgs) ? msgs[0] : String(msgs);
            errorEl.classList.add('palette-selector__card-error--visible');
          }
        }
      });
    } else {
      this.#showGlobalError(data.description ?? data.message ?? 'Erreur lors de l\'ajout au panier.');
    }
  }

  #showSuccess(bobines) {
    let el = this.querySelector('.palette-selector__success');
    if (!el) {
      el = document.createElement('div');
      el.className = 'palette-selector__success';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      this.querySelector('.palette-selector__footer')?.prepend(el);
    }
    el.textContent = `✓ ${bobines} bobine${bobines > 1 ? 's' : ''} ajoutée${bobines > 1 ? 's' : ''} au panier !`;
    el.classList.add('palette-selector__success--visible');
    setTimeout(() => el.classList.remove('palette-selector__success--visible'), 5000);
  }

  #showGlobalError(msg) {
    let el = this.querySelector('.palette-selector__global-error');
    if (!el) {
      el = document.createElement('p');
      el.className  = 'palette-selector__global-error';
      el.setAttribute('role', 'alert');
      this.querySelector('.palette-selector__footer')?.prepend(el);
    }
    el.textContent = msg;
    setTimeout(() => { if (el) el.textContent = ''; }, 5000);
  }

  /* ── Utilitaires ─────────────────────────────────────────────── */

  #money(cents) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
    }).format(cents / 100);
  }

  #esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('palette-selector', PaletteSelector);
